import React, { useEffect, useRef, useState } from "react";
import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";
import dayjs from "dayjs";
import { TbEdit } from "react-icons/tb";
import { FaLocationDot } from "react-icons/fa6";
import { useSnackbar } from "../SnackbarContext";
import { validateForm } from "../../utils/formValidation/validation";
import { personalInfoRules } from "../../utils/formValidation/validationRules";
import { getLatLngFromZip, reverseGeocode } from "../../utils/latLongFetch";
import { buildPersonalInfoPayload } from "../../utils/buildUserPayload";
import TextFieldCustom from "../elements/textfieldCustom";
import DatePickerCustom from "../elements/datePicker";
import RadioCustom from "../elements/radioCustom";
import CustomButton from "../elements/CustomButton/CustomButton";
import MapComponent from "../mapComponent";
import { gender } from "../../constants/formConstants";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

export default function PersonalInfoCard({ personalDetails, onSave }) {
  const showSnackbar = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobile: "",
    birthDate: null,
    gender: "",
    addressLine1: "",
    addressLine2: "",
    landmark: "",
    city: "",
    state: "",
    zip: "",
    latitude: null,
    longitude: null,
  });
  const [initialFormData, setInitialFormData] = useState({});
  const [isMapOpen, setIsMapOpen] = useState(false);
  const formDataWhenMapOpenedRef = useRef({
    latitude: null,
    longitude: null,
    addressLine1: "",
    landmark: "",
    city: "",
    state: "",
    zip: "",
  });

  useEffect(() => {
    if (personalDetails) {
      const data = {
        ...personalDetails,
        latitude: personalDetails.latitude ?? null,
        longitude: personalDetails.longitude ?? null,
      };
      setFormData(data);
      setInitialFormData(data);
    }
  }, [personalDetails]);

  const handleChange = (field) => (value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleInputChange = (field) => (e) =>
    handleChange(field)(e.target.value);

  const handleSave = async () => {
    const errors = validateForm(formData, personalInfoRules);
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      let updatedFormData = { ...formData };
      const hasMapPickedLocation =
        formData.latitude != null && formData.longitude != null;
      const needsUpdateLocation =
        (!personalDetails.latitude && !personalDetails.longitude) ||
        personalDetails.zipcode !== formData.zip;

      if (hasMapPickedLocation) {
        updatedFormData.latitude = formData.latitude;
        updatedFormData.longitude = formData.longitude;
        updatedFormData.address_id = personalDetails.addressId;
        updatedFormData.latlong_id = personalDetails.latlngID ?? undefined;
      } else if (needsUpdateLocation) {
        const latlong = await getLatLngFromZip(formData.zip);
        if (!latlong) {
          showSnackbar("Failed to get location from zip code", "error");
          return;
        }
        updatedFormData.latitude = latlong.latitude;
        updatedFormData.longitude = latlong.longitude;
        updatedFormData.address_id = personalDetails.addressId;
      } else {
        updatedFormData.latitude = personalDetails.latitude;
        updatedFormData.longitude = personalDetails.longitude;
        updatedFormData.address_id = personalDetails.addressId;
        updatedFormData.latlong_id = personalDetails.latlngID;
      }
      const payload = buildPersonalInfoPayload(updatedFormData);
      if (onSave) onSave(payload);
      setIsEditing(false);
    } else {
      showSnackbar("Please fix the errors before saving", "error");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center">
        <div className="font-semibold text-lg">Personal Information</div>
        <div className="flex gap-2">
          <CustomButton
              onClick={() => {
              if (isEditing) {
                setFormData(initialFormData);
                setIsMapOpen(false);
              }
              setIsEditing(!isEditing);
            }}
            customeClass="primary-custom-button w-[100px] h-[35px]"
          >
            <span className="mr-1 text-xl">{isEditing ? "" : <TbEdit />}</span>
            <span className="text-base">{isEditing ? "Cancel" : "Edit"}</span>
          </CustomButton>
          {isEditing && (
            <CustomButton
              onClick={handleSave}
              customeClass="gradient-button w-[100px] h-[35px]"
            >
              Save
            </CustomButton>
          )}
        </div>
      </div>

      {isEditing ? (
        <>
          <div className="grid md:grid-cols-2 gap-4 my-3">
            <TextFieldCustom
              label="Full Name*"
              fullWidth
              value={formData.fullName}
              onChange={handleInputChange("fullName")}
              error={!!formErrors.fullName}
              helperText={formErrors.fullName || " "}
            />
            
                         <div>
            <PhoneInput
              country={"us"}
              value={formData.mobile}
              onChange={(val) => handleChange("mobile")(val)}
                 inputClass="!w-full !h-[53px] !border !border-[#d1d5db] !rounded-[10px]"
              containerClass="w-full"
               disabled={!isEditing}
            />
            {formErrors.mobile && (
              <div className="text-red-600 text-xs mt-1">{formErrors.mobile}</div>
            )}
          </div>
            <TextFieldCustom
              disabled
              label="Email*"
              fullWidth
              value={formData.email}
            />
            <DatePickerCustom
              label="Birth Date*"
              value={formData.birthDate}
              onChange={handleChange("birthDate")}
              error={!!formErrors.birthDate}
              helperText={formErrors.birthDate || " "}
              maxDate={dayjs()}
            />
          </div>

          {/* Gender */}
          <FormControl
            component="fieldset"
            error={!!formErrors.gender}
            className="w-full"
          >
            <div className="font-medium text-gray-600">Gender</div>
            <RadioGroup
              row
              value={formData.gender}
              onChange={(e) => handleChange("gender")(e.target.value)}
            >
              {gender.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<RadioCustom />}
                  label={option.charAt(0).toUpperCase() + option.slice(1)}
                />
              ))}
            </RadioGroup>
            <FormHelperText>{formErrors.gender || " "}</FormHelperText>
          </FormControl>

          {/* Address */}
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-3">
              Address Details
            </div>

            <div className="mt-2">
              <TextFieldCustom
                label="Address*"
                fullWidth
                value={formData.addressLine1}
                onChange={handleInputChange("addressLine1")}
                error={!!formErrors.addressLine1}
                helperText={formErrors.addressLine1 || " "}
                heightClass="custom-textarea-height"
                multiline
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-0 gap-x-3">
              <TextFieldCustom
                label="Unit"
                fullWidth
                value={formData.landmark}
                onChange={handleInputChange("landmark")}
              />

              <TextFieldCustom
                label="City/Town*"
                fullWidth
                value={formData.city}
                onChange={handleInputChange("city")}
                error={!!formErrors.city}
                helperText={formErrors.city || " "}
              />

              <TextFieldCustom
                label="State*"
                fullWidth
                value={formData.state}
                onChange={handleInputChange("state")}
                error={!!formErrors.state}
                helperText={formErrors.state || " "}
              />

              <TextFieldCustom
                label="Zip code*"
                fullWidth
                value={formData.zip}
                onChange={handleInputChange("zip")}
                error={!!formErrors.zip}
                helperText={formErrors.zip || " "}
              />
            </div>

            <div className="flex items-center gap-3 mt-3">
              <button
                type="button"
                onClick={() => {
                  formDataWhenMapOpenedRef.current = {
                    latitude: formData.latitude,
                    longitude: formData.longitude,
                    addressLine1: formData.addressLine1 ?? "",
                    landmark: formData.landmark ?? "",
                    city: formData.city ?? "",
                    state: formData.state ?? "",
                    zip: formData.zip ?? "",
                  };
                  setIsMapOpen(true);
                }}
                className="text-[#1876A9] hover:text-[#02537F] hover:underline inline-flex items-center gap-2 text-sm font-medium"
              >
                <FaLocationDot className="text-lg" /> Pin location on map
              </button>
              {formData.latitude != null && formData.longitude != null && (
                <span className="text-xs text-gray-500">
                  Location set ({Number(formData.latitude).toFixed(5)}, {Number(formData.longitude).toFixed(5)})
                </span>
              )}
            </div>
            {isMapOpen && (
              <div className="mt-3 rounded-lg overflow-hidden border border-gray-200">
                <div className="flex justify-between items-center px-2 py-1.5 bg-gray-50 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Click on the map to set pin, then confirm or close</span>
                  <button
                    type="button"
                    onClick={() => {
                      const initial = formDataWhenMapOpenedRef.current;
                      setFormData((prev) => ({
                        ...prev,
                        latitude: initial.latitude,
                        longitude: initial.longitude,
                        addressLine1: initial.addressLine1,
                        landmark: initial.landmark,
                        city: initial.city,
                        state: initial.state,
                        zip: initial.zip,
                      }));
                      setIsMapOpen(false);
                    }}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded"
                    aria-label="Close map"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
                <MapComponent
                  isOpen={isMapOpen}
                  address={[formData.addressLine1, formData.city, formData.state, formData.zip].filter(Boolean).join(", ")}
                  onSelect={async (coords) => {
                    const address = await reverseGeocode(coords.lat, coords.lng);
                    setFormData((prev) => ({
                      ...prev,
                      latitude: coords.lat,
                      longitude: coords.lng,
                      ...(address && {
                        addressLine1: address.addressLine1 ?? prev.addressLine1,
                        landmark: address.landmark ?? prev.landmark,
                        city: address.city ?? prev.city,
                        state: address.state ?? prev.state,
                        zip: address.zip ?? prev.zip,
                      }),
                    }));
                  }}
                  height="320px"
                />
                <div className="flex justify-end gap-2 p-2 bg-gray-50">
                  <CustomButton
                    onClick={() => {
                      const initial = formDataWhenMapOpenedRef.current;
                      setFormData((prev) => ({
                        ...prev,
                        latitude: initial.latitude,
                        longitude: initial.longitude,
                        addressLine1: initial.addressLine1,
                        landmark: initial.landmark,
                        city: initial.city,
                        state: initial.state,
                        zip: initial.zip,
                      }));
                      setIsMapOpen(false);
                    }}
                    customeClass="primary-custom-button h-[36px] px-3"
                  >
                    Close
                  </CustomButton>
                  <CustomButton
                    onClick={() => setIsMapOpen(false)}
                    customeClass="gradient-button h-[36px] px-3"
                  >
                    Confirm location
                  </CustomButton>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Read-only */}
          <div className="grid md:grid-cols-3 gap-y-4 text-sm text-gray-700 mt-3">
            <div>
              <strong>Full Name</strong>
              <div className="mt-1">{formData.fullName || "-"}</div>
            </div>
            <div>
              <strong>Mobile Number</strong>
              <div className="mt-1">{formData.mobile || "-"}</div>
            </div>
            <div>
              <strong>Email</strong>
              <div className="mt-1">{formData.email || "-"}</div>
            </div>
            <div>
              <strong>Date of Birth</strong>
              <div className="mt-1">
                {formData.birthDate
                  ? dayjs(formData.birthDate).format("DD/MM/YYYY")
                  : "-"}
              </div>
            </div>
            <div>
              <strong>Gender</strong>
              <div className="mt-1">
                {formData.gender
                  ? formData.gender.charAt(0).toUpperCase() +
                    formData.gender.slice(1)
                  : "-"}
              </div>
            </div>
          </div>

          <div className="mt-6 text-sm text-gray-700">
            <strong>Address</strong>
            <div className="mt-1">
              {[
                formData.addressLine1,
                // formData.addressLine2,
                formData.landmark,
                formData.city,
                formData.state,
                formData.zip,
              ]
                .filter(Boolean)
                .join(", ") || "-"}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
